/* Common Imports */

import { styled, useTheme } from "@mui/system";
import React from "react";

/* Redux & Type Imports */

import { ObjectId } from "bson";
import { NextRouter } from "next/router";
import StateInterface from "../../../../redux-magic/state-interface";
import { ProfilePicture } from "../../../../redux-magic/sub-interfaces/business-profile-interface";
import { CurrentChatInterface } from "../../../../redux-magic/sub-interfaces/chat-states-interface";
import { acceptChatRequestThunk, declineChatRequestThunk, editChatMessageThunk, setChatEditingThunk, setChatReplyToThunk, sendChatMessageThunk, setChatScreenThunk, setCurrentChatThunk, acceptedBroadcastThunk } from "../../../../redux-magic/store";
/* Component Imports */

import { Alert, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Fade, IconButton, RadioGroup, TextField, Typography } from "@mui/material";
import BeegruButton from "../../../common-components/buttons/BeegruButton";

/* Icon Imports */

import {
	AddPhotoAlternateOutlined,
	ApartmentOutlined,
	AttachFileOutlined,
	CloseOutlined,
	ContactPhoneOutlined,
	LocationOnOutlined,
	DeleteOutline,
	MicNoneOutlined,
	PauseRounded,
	SendOutlined,
} from "@mui/icons-material";
import { ChatUploadFile, ChatUseCase, uploadChatFiles } from "../../../../lib/chatUpload";
import ChatLocationDialog, { SharedPlace } from "./ChatLocationDialog";
import ChatListingPickerDialog from "./ChatListingPickerDialog";

/* Library Functions */

import validateFiles from "../../../../lib/validateFiles";

/* Styled Components */

const RootDiv = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "column",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	gap: "0.5rem",
	background: theme.palette.background.default,
	padding: "0rem 0.75rem 1rem 0.75rem",
}));

const ChatContainer = styled("div")(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "space-between",
	alignItems: "flex-start",
	width: "100%",
	height: "100%",
	padding: "0.75rem 1rem 0.75rem 1rem",
	gap: "0.5rem",
	borderRadius: "2rem",
	boxShadow: "0px 4px 18px 0px rgba(0, 0, 0, 0.05)",
	background: theme.palette.background.paper,
	transition: "width 0.5s ease-in-out", // Add smooth width transition
}));

const AnimatedButtonWrapper = styled("div")(({ theme }) => ({
	display: "flex",
	transition: "all 0.2s ease-in-out",
	transform: "scale(1)",
	"&:hover": {
		transform: "scale(1.05)",
	},
}));

/* The recording dot (19 Oct 2026): red and breathing while recording, grey and still while paused. */
const RecordingDot = styled("span")(() => ({
	width: "0.5rem",
	height: "0.5rem",
	borderRadius: "50%",
	background: "#E31B0C",
	display: "inline-block",
	flexShrink: 0,
	animation: "chatRecordingDotPulse 1.8s ease-in-out infinite",
	"@keyframes chatRecordingDotPulse": {
		"0%, 100%": { opacity: 1 },
		"50%": { opacity: 0.35 },
	},
	"&.paused": {
		background: "#6c757d",
		animation: "none",
	},
	"@media (prefers-reduced-motion: reduce)": {
		animation: "none",
	},
}));

const InputContent = styled("input")(({ theme }) => ({
	display: "none",
}));

const ImagePreviewContainer = styled(RadioGroup)(({ theme }) => ({
	display: "flex",
	flexDirection: "row",
	justifyContent: "flex-start",
	alignItems: "flex-start",
	width: "100%",
	maxHeight: "30rem",
	gap: "1rem",
	flexWrap: "wrap",
}));

const PreviewGrid = styled("div")(({ theme }) => ({
	display: "grid",
	width: "100%",
	padding: "1rem 0",
	gap: "1rem",
	maxHeight: "420px",
	overflowY: "auto",
	overflowX: "hidden",
	[theme.breakpoints.down("sm")]: {
		gridTemplateColumns: "repeat(2, 1fr)",
	},
	[theme.breakpoints.between("sm", "md")]: {
		gridTemplateColumns: "repeat(2, 1fr)",
	},
	[theme.breakpoints.up("md")]: {
		gridTemplateColumns: "repeat(2, minmax(150px, 1fr))",
		maxWidth: "600px",
	},
}));
const ImageCard = styled("div")(({ theme }) => ({
	position: "relative",
	aspectRatio: "1",
	borderRadius: "1rem",
	overflow: "hidden",
	boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
	transition: "transform 0.2s ease-in-out",
	height: "150px", // Set fixed height
	"&:hover": {
		transform: "scale(1.02)",
	},
}));

const StyledPreviewImage = styled("img")({
	width: "100%",
	height: "100%",
	objectFit: "cover",
});

interface FileAndMore {
	file: File;
	mime_type: string;
}

const ChatInput = ({
	session,
	dispatch,
	router,
	realtime_websocket,
	profile_context,
	initial_load_done,
	messages_loading,
	chat_id,
	selectedFilePreview,
	pendingUploads,
	setPendingUploads,
	setSelectedFilePreview,
	current_message,
	setCurrentMessage,
	setSendFiles,
	sendFiles,
	messages_count,
	isBroadcastDeletedOrExpired,
	receiver_user_id,
	chat_source_collection,
	broadcast_id,
	property_id,
	chat_dp,
	current_chat_data,
	on_pending_text,
}: {
	session: StateInterface["redux_session"];
	dispatch: Function;
	router: NextRouter;
	realtime_websocket: any;
	profile_context: any;
	initial_load_done: boolean;
	messages_loading: boolean;
	chat_id: ObjectId | string;
	selectedFilePreview: Array<string>;
	pendingUploads: Array<FileAndMore>;
	setPendingUploads: React.Dispatch<React.SetStateAction<Array<FileAndMore>>>;
	setSelectedFilePreview: React.Dispatch<React.SetStateAction<Array<string>>>;
	current_message: string;
	setCurrentMessage: React.Dispatch<React.SetStateAction<string>>;
	setSendFiles: React.Dispatch<React.SetStateAction<boolean>>;
	sendFiles: boolean;
	messages_count: number;
	isBroadcastDeletedOrExpired: boolean;
	receiver_user_id: string | ObjectId;
	chat_source_collection: string | undefined;
	broadcast_id: ObjectId | string | undefined;
	property_id?: string | ObjectId | undefined;
	chat_dp?: ProfilePicture | string | undefined;
	current_chat_data?: CurrentChatInterface;
	/* A text on its way and its end (19 Oct 2026): the thread draws it with the clock meanwhile. */
	on_pending_text?: (change: { id: string; body: string } | { id: string; done: true }) => void;
}) => {
	const theme = useTheme();
	
	/* Translations */

	const localLang = {
		message: router.locale === "ar-AE" ? "رسالة" : router.locale === "kn-IN" ? "ಸಂದೇಶ" : "Message",
		start_typing:
			router.locale === "ar-AE"
				? "... بدء الكتابة"
				: router.locale === "kn-IN"
					? "ಟೈಪಿಂಗ್ ಪ್ರಾರಂಭಿಸಿ ..."
					: "Start typing ...",
	};

	const input = React.useRef<HTMLInputElement>(null);

	const [message, setMessage] = React.useState<string>("");

	const [fileErrorSnackbarText, setFileErrorSnackbarText] = React.useState<string>("File size or type not supported");

	const [openSnackbar, setOpenSnackbar] = React.useState(false);

	const [sendButtonDisabled, setSendButtonDisabled] = React.useState<boolean>(false);

	const [afterUpload, setAfterUpload] = React.useState<boolean>(false);


	/* Accepting a chat request (chat audit 2.2): one tap at a time, and a line the user can read if
	   it was refused. */
	const [acceptingRequest, setAcceptingRequest] = React.useState<boolean>(false);
	const [acceptRequestError, setAcceptRequestError] = React.useState<string | null>(null);
	/* Declining one (19 Oct 2026, chat/decline-chat-request): the request leaves the list for good. */
	const [decliningRequest, setDecliningRequest] = React.useState<boolean>(false);

	/* --- Documents and voice notes (chat audit I13, 17 Oct 2026), the app's way --- */

	const docInput = React.useRef<HTMLInputElement>(null);

	/* One attachment message on its way: "Sending…", or what failed with Try again (the upload is
	   kept, and the retry uses the same message id, so a send that did land is never doubled). */
	type AttachmentSend =
		| { state: "idle" }
		| { state: "sending"; label: string }
		| { state: "failed"; label: string; retry: () => void; discard: () => void };

	const [attachmentSend, setAttachmentSend] = React.useState<AttachmentSend>({ state: "idle" });

	const voiceLang = {
		sending_document:
			router.locale === "ar-AE" ? "جارٍ إرسال المستند…" : router.locale === "kn-IN" ? "ದಾಖಲೆ ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…" : "Sending document…",
		document_failed:
			router.locale === "ar-AE"
				? "تعذر إرسال المستند"
				: router.locale === "kn-IN"
					? "ದಾಖಲೆ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ"
					: "Couldn't send the document",
		sending_voice:
			router.locale === "ar-AE" ? "جارٍ إرسال الرسالة الصوتية…" : router.locale === "kn-IN" ? "ಧ್ವನಿ ಸಂದೇಶ ಕಳುಹಿಸಲಾಗುತ್ತಿದೆ…" : "Sending voice note…",
		voice_failed:
			router.locale === "ar-AE"
				? "تعذر إرسال الرسالة الصوتية"
				: router.locale === "kn-IN"
					? "ಧ್ವನಿ ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ"
					: "Couldn't send the voice note",
		try_again: router.locale === "ar-AE" ? "حاول مرة أخرى" : router.locale === "kn-IN" ? "ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ" : "Try again",
		discard: router.locale === "ar-AE" ? "إلغاء" : router.locale === "kn-IN" ? "ತಿರಸ್ಕರಿಸಿ" : "Discard",
		recording: router.locale === "ar-AE" ? "جارٍ التسجيل" : router.locale === "kn-IN" ? "ರೆಕಾರ್ಡ್ ಆಗುತ್ತಿದೆ" : "Recording",
		pause: router.locale === "ar-AE" ? "إيقاف مؤقت" : router.locale === "kn-IN" ? "ವಿರಾಮ" : "Pause",
		resume: router.locale === "ar-AE" ? "استئناف" : router.locale === "kn-IN" ? "ಮುಂದುವರಿಸಿ" : "Resume",
		too_short:
			router.locale === "ar-AE"
				? "التسجيل قصير جدًا"
				: router.locale === "kn-IN"
					? "ರೆಕಾರ್ಡಿಂಗ್ ತುಂಬಾ ಚಿಕ್ಕದಾಗಿದೆ"
					: "Too short to send. Record a little longer",
		mic_blocked:
			router.locale === "ar-AE"
				? "اسمح بالوصول إلى الميكروفون في متصفحك لتسجيل رسالة صوتية"
				: router.locale === "kn-IN"
					? "ಧ್ವನಿ ಸಂದೇಶ ರೆಕಾರ್ಡ್ ಮಾಡಲು ನಿಮ್ಮ ಬ್ರೌಸರ್‌ನಲ್ಲಿ ಮೈಕ್ರೊಫೋನ್ ಅನುಮತಿಸಿ"
					: "Allow the microphone in your browser to record a voice note",
		voice_unsupported:
			router.locale === "ar-AE"
				? "لا يدعم هذا المتصفح الرسائل الصوتية"
				: router.locale === "kn-IN"
					? "ಈ ಬ್ರೌಸರ್ ಧ್ವನಿ ಸಂದೇಶಗಳನ್ನು ಬೆಂಬಲಿಸುವುದಿಲ್ಲ"
					: "This browser can't record voice notes",
		replying_to: router.locale === "ar-AE" ? "الرد على" : router.locale === "kn-IN" ? "ಇದಕ್ಕೆ ಉತ್ತರ" : "Replying to",
		editing: router.locale === "ar-AE" ? "تعديل الرسالة" : router.locale === "kn-IN" ? "ಸಂದೇಶವನ್ನು ಸಂಪಾದಿಸಲಾಗುತ್ತಿದೆ" : "Editing message",
		edit_failed:
			router.locale === "ar-AE"
				? "تعذر تعديل الرسالة. يرجى المحاولة مرة أخرى."
				: router.locale === "kn-IN"
					? "ಸಂದೇಶವನ್ನು ಸಂಪಾದಿಸಲು ಸಾಧ್ಯವಾಗಲಿಲ್ಲ. ದಯವಿಟ್ಟು ಮತ್ತೆ ಪ್ರಯತ್ನಿಸಿ."
					: "Couldn't edit the message. Please try again",
	};

	/* --- Editing (18 Oct 2026, the app's) ---
	   The thread's Edit puts the message's text in the composer under an "Editing message" strip;
	   sending saves it (chat/edit-message), the cross stops. */

	const editing = current_chat_data?.editing || null;
	const [editError, setEditError] = React.useState<string | null>(null);
	const editingIdRef = React.useRef<string>("");
	React.useEffect(() => {
		const id: string = editing?.message_id || "";
		if (id && id !== editingIdRef.current) {
			setMessage(editing?.body || "");
			setEditError(null);
		} else if (!id && editingIdRef.current) {
			// The edit ended (saved, cancelled, or a reply took over): the composer empties.
			setMessage("");
		}
		editingIdRef.current = id;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editing?.message_id]);

	const savingEditRef = React.useRef<boolean>(false);
	const saveEdit = async (text: string) => {
		if (!editing || !text.trim() || savingEditRef.current) return;
		savingEditRef.current = true;
		const outcome = (await dispatch(
			editChatMessageThunk({
				chat_id: chat_id,
				message_id: editing.message_id,
				message_body: text.trim(),
				is_business_profile: profile_context.is_business_profile === true,
				user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
				business_profile_id: profile_context.business_profile_id,
			}),
		)) as { ok: boolean; error: string | null } | undefined;
		savingEditRef.current = false;
		if (outcome?.ok) {
			dispatch(setChatEditingThunk(null));
		} else {
			setEditError(outcome?.error || voiceLang.edit_failed);
		}
	};

	/* Uploads [items] as one message of their own (a document or a voice note, sent alone as in
	   the app), then sends it. */
	const sendAttachments = async (items: Array<ChatUploadFile>, useCase: ChatUseCase, sendingLabel: string, failedLabel: string) => {
		const message_id: string = new ObjectId().toString();
		// The quoted message, if the viewer is replying, taken once so a retry quotes the same one.
		const replyToId: string | undefined = current_chat_data?.reply_to?.message_id || undefined;
		let uploaded: Array<{ file_id: string; mime_type: string }> | null = null;
		const discard = () => setAttachmentSend({ state: "idle" });
		const attempt = async () => {
			setAttachmentSend({ state: "sending", label: sendingLabel });
			if (!uploaded) {
				uploaded = await uploadChatFiles({
					session,
					profile_context,
					chatId: String(chat_id),
					files: items,
					useCase,
				});
			}
			if (!uploaded) {
				setAttachmentSend({ state: "failed", label: failedLabel, retry: attempt, discard });
				return;
			}
			const outcome = (await dispatch(
				sendChatMessageThunk({
					chat_id: chat_id,
					message_id: message_id,
					sender_is_business_profile: profile_context.is_business_profile,
					sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					sender_business_profile_id: profile_context.business_profile_id,
					message_body: "",
					attachments: uploaded,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
					...(replyToId ? { reply_to: replyToId } : {}),
				}),
			)) as { ok: boolean } | undefined;
			if (!outcome || !outcome.ok) {
				setAttachmentSend({ state: "failed", label: failedLabel, retry: attempt, discard });
				return;
			}
			setAttachmentSend({ state: "idle" });
			if (replyToId) dispatch(setChatReplyToThunk(null));
		};
		await attempt();
	};

	/* PDFs picked from the paperclip: each is its own message, as the app sends documents. */
	const handlePickDocuments = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const picked: Array<File> = Array.from(event.target.files || []);
		event.target.value = "";
		const pdfs: Array<File> = picked.filter(
			(file: File) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"),
		);
		if (pdfs.length < picked.length) {
			setFileErrorSnackbarText(
				router.locale === "ar-AE"
					? "يمكن إرسال ملفات PDF فقط كمستندات"
					: router.locale === "kn-IN"
						? "ದಾಖಲೆಗಳಾಗಿ PDF ಮಾತ್ರ ಕಳುಹಿಸಬಹುದು"
						: "Only PDFs can be sent as documents",
			);
			setOpenSnackbar(true);
		}
		for (const pdf of pdfs) {
			await sendAttachments(
				[{ file: pdf, mime_type: "application/pdf" }],
				"chat_file",
				voiceLang.sending_document,
				voiceLang.document_failed,
			);
		}
	};

	/* Voice notes: click the mic to record, then send or throw it away (a browser has no hold to
	   record). Recorded in whatever the browser makes (WebM / Opus on most, MP4 on Safari), every
	   one of which chat plays (lib/chat/chatAttachmentKinds.ts). At most 10 minutes, the app's
	   limit; under a second is too short to send. */

	const VOICE_NOTE_MAX_SECONDS: number = 10 * 60;
	const [recordingStartedAt, setRecordingStartedAt] = React.useState<number | null>(null);
	const [recordingSeconds, setRecordingSeconds] = React.useState<number>(0);
	const [voiceNotice, setVoiceNotice] = React.useState<string | null>(null);
	const recorderRef = React.useRef<MediaRecorder | null>(null);
	const recordedChunksRef = React.useRef<Array<Blob>>([]);
	const discardRecordingRef = React.useRef<boolean>(false);
	/* Pause and resume (19 Oct 2026): the time spent paused, and when the current pause began, so
	   the clock, the limit and the note's length count only recorded time. */
	const [recordingPaused, setRecordingPaused] = React.useState<boolean>(false);
	const pausedTotalMsRef = React.useRef<number>(0);
	const pauseStartedAtRef = React.useRef<number | null>(null);
	/* The live waveform (19 Oct 2026): the microphone's level read off the same stream, one bar about
	   every 100 ms, drawn on a canvas so the input never re-renders for it. */
	const WAVEFORM_SAMPLE_MS: number = 100;
	const WAVEFORM_BAR_WIDTH: number = 3;
	const WAVEFORM_BAR_GAP: number = 2;
	const audioContextRef = React.useRef<AudioContext | null>(null);
	const analyserRef = React.useRef<AnalyserNode | null>(null);
	const waveformCanvasRef = React.useRef<HTMLCanvasElement | null>(null);
	const waveformLevelsRef = React.useRef<Array<number>>([]);

	// Recorded time so far: wall time since the start, less every pause (the current one included).
	const recordedMillis = (startedAt: number, now: number): number => {
		const currentPause: number = pauseStartedAtRef.current !== null ? now - pauseStartedAtRef.current : 0;
		return Math.max(0, now - startedAt - pausedTotalMsRef.current - currentPause);
	};

	// Stops the level reading and frees the audio graph (recording sent, thrown away, or the chat left).
	const teardownWaveform = () => {
		const context: AudioContext | null = audioContextRef.current;
		audioContextRef.current = null;
		analyserRef.current = null;
		waveformLevelsRef.current = [];
		if (context && context.state !== "closed") {
			context.close().catch(() => undefined);
		}
	};

	// Starts reading the microphone level; without Web Audio there is simply no waveform.
	const startWaveform = (stream: MediaStream) => {
		teardownWaveform();
		const AudioContextClass: typeof AudioContext | undefined =
			window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
		if (!AudioContextClass) return;
		let context: AudioContext | null = null;
		try {
			context = new AudioContextClass();
			const source: MediaStreamAudioSourceNode = context.createMediaStreamSource(stream);
			const analyser: AnalyserNode = context.createAnalyser();
			analyser.fftSize = 1024;
			source.connect(analyser);
			if (context.state === "suspended") context.resume().catch(() => undefined);
			audioContextRef.current = context;
			analyserRef.current = analyser;
		} catch {
			if (context && context.state !== "closed") context.close().catch(() => undefined);
		}
	};

	const pickVoiceMime = (): string => {
		const options: Array<string> = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus", "audio/ogg"];
		for (const option of options) {
			try {
				if (MediaRecorder.isTypeSupported(option)) return option;
			} catch {
				// Not supported; try the next.
			}
		}
		return "";
	};

	const stopRecording = (discard: boolean) => {
		discardRecordingRef.current = discard;
		const recorder = recorderRef.current;
		if (recorder && recorder.state !== "inactive") recorder.stop();
	};

	// Pause or resume the recording (19 Oct 2026); stopping while paused still sends what was recorded.
	const togglePauseRecording = () => {
		const recorder = recorderRef.current;
		if (!recorder) return;
		if (recorder.state === "recording") {
			try {
				recorder.pause();
			} catch {
				return;
			}
			pauseStartedAtRef.current = Date.now();
			setRecordingPaused(true);
		} else if (recorder.state === "paused") {
			try {
				recorder.resume();
			} catch {
				return;
			}
			if (pauseStartedAtRef.current !== null) pausedTotalMsRef.current += Date.now() - pauseStartedAtRef.current;
			pauseStartedAtRef.current = null;
			setRecordingPaused(false);
		}
	};

	const startRecording = async () => {
		setVoiceNotice(null);
		if (recordingStartedAt !== null) return;
		if (typeof window === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
			setVoiceNotice(voiceLang.voice_unsupported);
			return;
		}
		let stream: MediaStream;
		try {
			stream = await navigator.mediaDevices.getUserMedia({ audio: true });
		} catch {
			setVoiceNotice(voiceLang.mic_blocked);
			return;
		}
		const mime: string = pickVoiceMime();
		let recorder: MediaRecorder;
		try {
			recorder = mime ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 64000 }) : new MediaRecorder(stream);
		} catch {
			stream.getTracks().forEach((track) => track.stop());
			setVoiceNotice(voiceLang.voice_unsupported);
			return;
		}
		recordedChunksRef.current = [];
		discardRecordingRef.current = false;
		pausedTotalMsRef.current = 0;
		pauseStartedAtRef.current = null;
		setRecordingPaused(false);
		const startedAt: number = Date.now();
		recorder.ondataavailable = (event: BlobEvent) => {
			if (event.data && event.data.size > 0) recordedChunksRef.current.push(event.data);
		};
		recorder.onstop = () => {
			// Recorded time only, read before the pause state is cleared (19 Oct 2026).
			const seconds: number = recordedMillis(startedAt, Date.now()) / 1000;
			teardownWaveform();
			stream.getTracks().forEach((track) => track.stop());
			recorderRef.current = null;
			pausedTotalMsRef.current = 0;
			pauseStartedAtRef.current = null;
			setRecordingPaused(false);
			setRecordingStartedAt(null);
			setRecordingSeconds(0);
			if (discardRecordingRef.current) return;
			if (seconds < 1) {
				setVoiceNotice(voiceLang.too_short);
				return;
			}
			// The plain type, without codecs: what the upload is signed for and what chat accepts.
			const baseMime: string = String(recorder.mimeType || mime || "audio/webm").split(";")[0].trim() || "audio/webm";
			const extension: string = baseMime === "audio/mp4" ? "m4a" : baseMime === "audio/ogg" ? "ogg" : "webm";
			const file: File = new File([new Blob(recordedChunksRef.current, { type: baseMime })], `voice-note.${extension}`, {
				type: baseMime,
			});
			sendAttachments(
				[{ file, mime_type: baseMime, duration: Math.round(seconds) }],
				"chat_audio",
				voiceLang.sending_voice,
				voiceLang.voice_failed,
			);
		};
		recorderRef.current = recorder;
		recorder.start();
		startWaveform(stream);
		setRecordingStartedAt(startedAt);
		setRecordingSeconds(0);
	};

	// The clock while recording, and the 10-minute limit (the note is sent at the limit), both in
	// recorded time, so a pause stops the clock (19 Oct 2026).
	React.useEffect(() => {
		if (recordingStartedAt === null) return;
		const timer = setInterval(() => {
			const seconds: number = Math.floor(recordedMillis(recordingStartedAt, Date.now()) / 1000);
			setRecordingSeconds(seconds);
			if (seconds >= VOICE_NOTE_MAX_SECONDS) stopRecording(false);
		}, 250);
		return () => clearInterval(timer);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recordingStartedAt]);

	/* The waveform's drawing loop (19 Oct 2026): a new bar about every 100 ms, newest on the right,
	   older ones scrolling left; paused, the bars hold still and dim. Drawn only when something changed. */
	React.useEffect(() => {
		if (recordingStartedAt === null) return;
		let frame: number = 0;
		let lastSampleAt: number = 0;
		let lastDrawnKey: string = "";
		let samples: Uint8Array<ArrayBuffer> | null = null;
		const draw = (now: number) => {
			frame = requestAnimationFrame(draw);
			const analyser: AnalyserNode | null = analyserRef.current;
			const canvas: HTMLCanvasElement | null = waveformCanvasRef.current;
			if (!analyser || !canvas) return;
			const paused: boolean = recorderRef.current?.state === "paused";
			const ratio: number = window.devicePixelRatio || 1;
			const cssWidth: number = canvas.clientWidth;
			const cssHeight: number = canvas.clientHeight;
			const fits: number = Math.max(1, Math.floor((cssWidth + WAVEFORM_BAR_GAP) / (WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_GAP)));
			const levels: Array<number> = waveformLevelsRef.current;
			if (!paused && now - lastSampleAt >= WAVEFORM_SAMPLE_MS) {
				lastSampleAt = now;
				if (!samples || samples.length !== analyser.fftSize) samples = new Uint8Array(analyser.fftSize);
				analyser.getByteTimeDomainData(samples);
				let peak: number = 0;
				for (let index = 0; index < samples.length; index++) {
					const value: number = Math.abs(samples[index] - 128) / 128;
					if (value > peak) peak = value;
				}
				// Decibels over a 50 dB range, so quiet speech still shows (0.1 is the floor).
				const decibels: number = 20 * Math.log10(Math.max(peak, 0.0001));
				const normalised: number = Math.min(1, Math.max(0, (decibels + 50) / 50));
				levels.push(0.1 + 0.9 * normalised);
				if (levels.length > fits) levels.splice(0, levels.length - fits);
			}
			const key: string = `${levels.length}:${lastSampleAt}:${paused}:${cssWidth}x${cssHeight}:${ratio}`;
			if (key === lastDrawnKey) return;
			lastDrawnKey = key;
			const width: number = Math.round(cssWidth * ratio);
			const height: number = Math.round(cssHeight * ratio);
			if (canvas.width !== width) canvas.width = width;
			if (canvas.height !== height) canvas.height = height;
			const context: CanvasRenderingContext2D | null = canvas.getContext("2d");
			if (!context) return;
			context.setTransform(ratio, 0, 0, ratio, 0, 0);
			context.clearRect(0, 0, cssWidth, cssHeight);
			context.fillStyle = "#6c757d";
			context.globalAlpha = paused ? 0.45 : 1;
			const shown: Array<number> = levels.slice(-fits);
			shown.forEach((level: number, index: number) => {
				const barHeight: number = Math.max(WAVEFORM_BAR_WIDTH, level * cssHeight);
				const x: number = cssWidth - (shown.length - index) * (WAVEFORM_BAR_WIDTH + WAVEFORM_BAR_GAP) + WAVEFORM_BAR_GAP;
				const y: number = (cssHeight - barHeight) / 2;
				context.beginPath();
				if (typeof context.roundRect === "function") {
					context.roundRect(x, y, WAVEFORM_BAR_WIDTH, barHeight, WAVEFORM_BAR_WIDTH / 2);
				} else {
					context.rect(x, y, WAVEFORM_BAR_WIDTH, barHeight);
				}
				context.fill();
			});
		};
		frame = requestAnimationFrame(draw);
		return () => cancelAnimationFrame(frame);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [recordingStartedAt]);

	// Leaving the chat mid-recording throws the recording away and frees the microphone (and, since
	// 19 Oct 2026, the waveform's audio graph).
	React.useEffect(() => {
		return () => {
			stopRecording(true);
			teardownWaveform();
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const recordingClock: string = `${Math.floor(recordingSeconds / 60)}:${String(recordingSeconds % 60).padStart(2, "0")}`;

	const handleAcceptChatRequest = async () => {
		if (acceptingRequest) return;
		setAcceptingRequest(true);
		setAcceptRequestError(null);
		const outcome = (await dispatch(
			acceptChatRequestThunk({
				chat_id: String(current_chat_data?.chat_id ?? chat_id),
				is_business_profile: profile_context?.is_business_profile === true,
				user_id: session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "",
				business_profile_id: profile_context?.business_profile_id || "",
			}),
		)) as { ok: boolean; error: string | null } | undefined;
		setAcceptingRequest(false);
		if (!outcome || !outcome.ok) {
			setAcceptRequestError(outcome?.error || "Something went wrong. Please try again");
		}
	};

	/* Decline: nobody is told, the row leaves the list and the thread closes (the thunk does both). */
	const [declineConfirmOpen, setDeclineConfirmOpen] = React.useState<boolean>(false);
	const handleDeclineChatRequest = async () => {
		if (decliningRequest || acceptingRequest) return;
		setDecliningRequest(true);
		setAcceptRequestError(null);
		const ok = (await dispatch(
			declineChatRequestThunk({
				chat_id: String(current_chat_data?.chat_id ?? chat_id),
				is_business_profile: profile_context?.is_business_profile === true,
				user_id: session?.user?.dbUser?._id ? String(session.user.dbUser._id) : "",
				business_profile_id: profile_context?.business_profile_id || "",
			}),
		)) as boolean | undefined;
		setDecliningRequest(false);
		if (!ok) {
			setAcceptRequestError("Something went wrong. Please try again");
		}
	};


	/* Handle Textfield Change */

	/* "I'm typing" (18 Oct 2026, chat/typing, lib/chat/typing.ts): at most every few seconds while
	   there is text, so the other side shows "typing…". Quiet: a failure only hides it. */
	const TYPING_SEND_EVERY_MS: number = 4000;
	const lastTypingSentAt = React.useRef<number>(0);
	const sayTyping = () => {
		const now: number = Date.now();
		if (now - lastTypingSentAt.current < TYPING_SEND_EVERY_MS) return;
		lastTypingSentAt.current = now;
		fetch(process.env.PRODUCTION_API_URL + "chat/typing", {
			method: "POST",
			cache: "no-store",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({
				chat_id: String(chat_id),
				is_business_profile: profile_context?.is_business_profile === true,
				user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
				business_profile_id: profile_context?.business_profile_id || "",
			}),
		}).catch(() => {
			// Only a hint.
		});
	};

	const handleTextFieldChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (afterUpload) {
			setCurrentMessage(event.target.value);
		} else {
			setMessage(event.target.value);
		}
		if (event.target.value.trim() !== "") sayTyping();
	};

	/* Send Message Function */

	/*

	& One text send (chat audit I3 / D11, 17 Oct 2026). Whitespace-only text is never sent, a
	& second send while one is in flight is ignored (a double click on Send or a quick reply used
	& to send twice). The other side hears of it from the server once it is stored
	& (chat/send-message relays `new-chat-message` by user id, 18 Oct 2026).

	*/

	const sendingTextRef = React.useRef<boolean>(false);

	/*

	& A broadcast from someone the viewer never chatted with has no chat (18 Oct 2026,
	& lib/broadcasts/broadcastRequest.ts): its list row is the broadcast-recipients row, and the
	& thread here is its card. The first message opens the chat (chat/open-broadcast-chat: the card
	& is posted first, the chat accepted), goes into it, and the thread then moves into that chat.

	*/

	const openBroadcastChat = async (): Promise<string | null> => {
		try {
			const res = await fetch(`${process.env.PRODUCTION_API_URL}chat/open-broadcast-chat`, {
				method: "POST",
				cache: "no-store",
				body: JSON.stringify({
					broadcast_id: broadcast_id,
					is_business_profile: profile_context.is_business_profile,
					user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					business_profile_id: profile_context.business_profile_id,
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
				}),
			});
			const data = await res.json();
			return data?.status === "success" && data?.payload?.chat_id ? String(data.payload.chat_id) : null;
		} catch (error) {
			return null;
		}
	};

	/* The thread becomes the chat [realChatId]: its row leaves the list (which reloads with the real
	   chat) and the chat opens in place, reading its messages (the card and what was just sent). */
	const moveIntoChat = (realChatId: string) => {
		dispatch(acceptedBroadcastThunk({ broadcast_id: chat_id, chat_id: realChatId }));
		dispatch(
			setCurrentChatThunk({
				chat_id: realChatId,
				chat_type: "direct",
				chat_title: "",
				chat_description: "",
				chat_dp: {
					file_id: typeof chat_dp === "object" && chat_dp ? chat_dp.file_id : chat_dp || "",
					exists: chat_dp ? true : false,
					original_file_server_path: "",
				},
				chat_is_broadcast: false,
				chat_notification_settings: {
					is_business_profile: profile_context.is_business_profile,
					user_id: profile_context.user_id,
					business_profile_id: profile_context.business_profile_id,
					is_muted: false,
					muted_till: "",
				},
				chat_participants: current_chat_data?.participants || [],
				chat_created_at: current_chat_data?.created_at || new Date(),
				chat_source_collection: "chat",
				chat_last_message: current_chat_data?.chat_last_message,
				chat_broadcast_id: undefined,
				property_id: property_id,
				invite_status: undefined,
			}),
		);
	};

	/*

	& A location or the business's own contact card (decision D10, the app's attach sheet; on the
	& website since 18 Oct 2026). The server writes the message (lib/chat/locationContact.ts); each is
	& sent alone, and a refusal meant for the user is said in the snackbar.

	*/

	const [locationDialogOpen, setLocationDialogOpen] = React.useState<boolean>(false);
	const sendingSpecialRef = React.useRef<boolean>(false);

	const sendSpecial = async (special: {
		location?: SharedPlace;
		share_contact?: boolean;
		/* Your listings, shared on their own (gap A8); an open reply quotes them, as in the app. */
		shared_entity?: Array<{ entity_type: "property"; entity_id: string }>;
		reply_to?: string;
	}) => {
		if (sendingSpecialRef.current) return;
		sendingSpecialRef.current = true;
		const noChatYet: boolean = chat_source_collection === "broadcast";
		const targetChatId: string | null = noChatYet ? await openBroadcastChat() : String(chat_id);
		try {
			if (!targetChatId) throw new Error("no chat");
			const outcome = (await dispatch(
				sendChatMessageThunk({
					chat_id: targetChatId,
					message_id: new ObjectId().toString(),
					sender_is_business_profile: profile_context.is_business_profile,
					sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					sender_business_profile_id: profile_context.business_profile_id,
					message_body: "",
					attachments: [],
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
					...special,
				}),
			)) as { ok: boolean; error?: string } | undefined;
			if (!outcome?.ok) {
				setFileErrorSnackbarText(outcome?.error || "Something went wrong. Please try again");
				setOpenSnackbar(true);
			} else {
				if (special.reply_to) dispatch(setChatReplyToThunk(null));
				if (noChatYet) moveIntoChat(targetChatId);
			}
		} catch (error) {
			setFileErrorSnackbarText("Something went wrong. Please try again");
			setOpenSnackbar(true);
		} finally {
			sendingSpecialRef.current = false;
		}
	};

	/* "Attach listings (Maximum 5)" (gap A8). */
	const [listingPickerOpen, setListingPickerOpen] = React.useState<boolean>(false);
	const shareListings = (propertyIds: Array<string>) => {
		setListingPickerOpen(false);
		const replyToId: string | undefined = current_chat_data?.reply_to?.message_id || undefined;
		sendSpecial({
			shared_entity: propertyIds.map((id) => ({ entity_type: "property" as const, entity_id: id })),
			...(replyToId ? { reply_to: replyToId } : {}),
		});
	};

	const sendTextMessage = async (text: string) => {
		if (!text.trim() || sendingTextRef.current) return;
		sendingTextRef.current = true;
		const message_id = new ObjectId();
		// The quoted message, if the viewer is replying (chat audit I13).
		const replyToId: string | undefined = current_chat_data?.reply_to?.message_id || undefined;
		// No chat yet (a broadcast nobody has acted on): this message opens it.
		const noChatYet: boolean = chat_source_collection === "broadcast";
		// The thread shows it with the clock until the server has stored it.
		on_pending_text?.({ id: message_id.toString(), body: text });
		const targetChatId: string | null = noChatYet ? await openBroadcastChat() : String(chat_id);
		if (!targetChatId) {
			// Nothing was sent: the text goes back in the box to try again.
			on_pending_text?.({ id: message_id.toString(), done: true });
			setMessage(text);
			sendingTextRef.current = false;
			return;
		}
		try {
			const outcome = (await dispatch(
				sendChatMessageThunk({
					chat_id: targetChatId,
					message_id: message_id.toString(),
					sender_is_business_profile: profile_context.is_business_profile,
					sender_user_id: session ? (session.user ? session.user.dbUser._id : "") : "",
					sender_business_profile_id: profile_context.business_profile_id,
					message_body: text,
					attachments: [],
					sessionId: session ? (session.session_id ? session.session_id : "") : "",
					...(replyToId ? { reply_to: replyToId } : {}),
				}),
			)) as { ok: boolean } | undefined;
			if (outcome?.ok && replyToId) {
				dispatch(setChatReplyToThunk(null));
			}
			if (!outcome?.ok) throw new Error("not sent");
			// The chat exists now, so the thread moves into it (it re-reads the card and this message).
			if (noChatYet) moveIntoChat(targetChatId);
		} catch (error) {
			/* Not stored (3 Nov 2026, the app's "Send failed"): the text goes back in the box, unless something new
			   was typed meanwhile, and the snackbar says so; sending again retries it. */
			setMessage((current) => (current.trim() ? current : text));
			setFileErrorSnackbarText("Message not sent. Press send to try again.");
			setOpenSnackbar(true);
		} finally {
			on_pending_text?.({ id: message_id.toString(), done: true });
			sendingTextRef.current = false;
		}
	};

	const handleSendMessage = () => {
		// While editing, sending saves the edit (the text stays until it is saved).
		if (editing && !afterUpload) {
			saveEdit(message);
			return;
		}
		if (afterUpload) {
			setSendFiles(true);
			setSendButtonDisabled(true);
			setAfterUpload(false);
		} else {
			sendTextMessage(message);
		}
		setMessage("");
	};

	const handleRemoveImage = async (index: number) => {
		const newSelectedFilePreview = [...selectedFilePreview];
		const newPendingUploads = [...pendingUploads];

		// Revoke the blob URL to free up memory
		URL.revokeObjectURL(selectedFilePreview[index]);

		newSelectedFilePreview.splice(index, 1);
		newPendingUploads.splice(index, 1);

		setSelectedFilePreview(newSelectedFilePreview);
		setPendingUploads(newPendingUploads);
	};

	React.useEffect(() => {
		if (pendingUploads.length > 0) {
			setSendButtonDisabled(false);
		}
	}, [pendingUploads]);

	/* A blocked pair (chat/fetch-messages `is_blocked`, or a send the server refused for it), or a
	   counterpart business that is no longer live (`is_read_only`): the thread stays readable and
	   the composer is frozen, as in the apps. */
	if (current_chat_data?.is_blocked === true || current_chat_data?.is_read_only === true) {
		return (
			<RootDiv>
				<Typography
					variant="body2"
					sx={{ width: "100%", textAlign: "center", color: "text.secondary", padding: "0.75rem 0rem" }}
				>
					{current_chat_data?.is_read_only === true
						? router.locale === "ar-AE"
							? "هذا الملف الشخصي لم يعد متاحًا"
							: router.locale === "kn-IN"
								? "ಈ ಪ್ರೊಫೈಲ್ ಇನ್ನು ಮುಂದೆ ಲಭ್ಯವಿಲ್ಲ"
								: "This profile is no longer available"
						: router.locale === "ar-AE"
							? "لا يمكنك مراسلة هذا الملف الشخصي"
							: router.locale === "kn-IN"
								? "ನೀವು ಈ ಪ್ರೊಫೈಲ್‌ಗೆ ಸಂದೇಶ ಕಳುಹಿಸಲು ಸಾಧ್ಯವಿಲ್ಲ"
								: "You can't message this profile"}
				</Typography>
			</RootDiv>
		);
	}

	/* A chat request (chat/fetch-messages `is_request`, chat audit 2.2): read-only, and invisible to
	   the other side, until the viewer accepts it. The app's bar: Decline and Accept chat. */
	if (current_chat_data?.is_request === true) {
		return (
			<RootDiv sx={{ flexDirection: "column", gap: "0.625rem", padding: "0.75rem 1rem" }}>
				<Typography variant="body2" sx={{ width: "100%", textAlign: "center", color: "text.secondary", fontSize: "0.8125rem" }}>
					{router.locale === "ar-AE"
						? "لن يرى الطرف الآخر هذه المحادثة حتى تقبلها"
						: router.locale === "kn-IN"
							? "ನೀವು ಸ್ವೀಕರಿಸುವವರೆಗೆ ಅವರು ಈ ಚಾಟ್ ನೋಡುವುದಿಲ್ಲ"
							: "They won't see this chat until you accept"}
				</Typography>
				{acceptRequestError ? (
					<Typography variant="body2" sx={{ width: "100%", textAlign: "center", color: "error.main", fontSize: "0.8125rem" }}>
						{acceptRequestError}
					</Typography>
				) : null}
				{/* Decline beside Accept, 50:50, the lesser one outlined (19 Oct 2026, the app's bar). */}
				<div style={{ display: "flex", flexDirection: "row", gap: "0.625rem", width: "100%" }}>
					<BeegruButton
						flavor="primary"
						variant="outlined"
						disabled={acceptingRequest || decliningRequest}
						onClick={() => setDeclineConfirmOpen(true)}
						sx={{ flex: 1, textTransform: "none", borderRadius: "0.75rem" }}
					>
						{decliningRequest ? (
							<CircularProgress size={20} sx={{ color: "inherit" }} />
						) : router.locale === "ar-AE" ? (
							"رفض"
						) : router.locale === "kn-IN" ? (
							"ನಿರಾಕರಿಸಿ"
						) : (
							"Decline"
						)}
					</BeegruButton>
					<BeegruButton
						flavor="primary"
						variant="contained"
						disabled={acceptingRequest || decliningRequest}
						onClick={handleAcceptChatRequest}
						sx={{ flex: 1, textTransform: "none", borderRadius: "0.75rem" }}
					>
						{acceptingRequest ? (
							<CircularProgress size={20} sx={{ color: "inherit" }} />
						) : router.locale === "ar-AE" ? (
							"قبول المحادثة"
						) : router.locale === "kn-IN" ? (
							"ಚಾಟ್ ಸ್ವೀಕರಿಸಿ"
						) : (
							"Accept chat"
						)}
					</BeegruButton>
				</div>
				{/* Asked first (3 Nov 2026, the app's): a declined request never comes back. */}
				<Dialog
					open={declineConfirmOpen}
					onClose={() => setDeclineConfirmOpen(false)}
					maxWidth="xs"
					fullWidth
				>
					<DialogTitle>Decline this chat request?</DialogTitle>
					<DialogContent>
						<DialogContentText>
							It leaves your chats and they won&apos;t be told. If they write again, it stays declined.
						</DialogContentText>
					</DialogContent>
					<DialogActions>
						<BeegruButton
							variant="outlined"
							flavor="primary"
							size="small"
							onClick={() => setDeclineConfirmOpen(false)}
						>
							Cancel
						</BeegruButton>
						<BeegruButton
							variant="contained"
							flavor="error"
							size="small"
							onClick={() => {
								setDeclineConfirmOpen(false);
								handleDeclineChatRequest();
							}}
						>
							Decline
						</BeegruButton>
					</DialogActions>
				</Dialog>
			</RootDiv>
		);
	}

	return (
		<RootDiv>
			{/* File Upload Field */}

			<InputContent
				ref={input}
				type="file"
				accept="image/jpeg, image/png"
				multiple
				onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
					const files = event.target.files;
					const allowedTypes = ["image/jpeg", "image/png"];
					const allowedExtensions = ["jpg", "jpeg", "png"];
					const uploadType: string = "property_or_services";

					if (files && files.length > 0) {
						const {
							validFiles,
							invalidFiles,
						}: {
							validFiles: Array<FileAndMore>;
							invalidFiles: Array<File>;
						} = await validateFiles(Array.from(files), allowedTypes, allowedExtensions, uploadType);

						if (validFiles.length > 0) {
							// Create blob URLs for preview
							setAfterUpload(true);
							const previews = validFiles.map((f) => URL.createObjectURL(f.file));
							setSelectedFilePreview((prev) => [...prev, ...previews]);
							setPendingUploads((prev) => [...prev, ...validFiles]);
							setCurrentMessage(message);
							setMessage("");
						}

						if (invalidFiles.length > 0) {
							const fileNames: Array<string> = invalidFiles.map((file: File) => file.name);
							setFileErrorSnackbarText(
								(fileNames.join(", ").length > 15 ? fileNames.join(", ").slice(0, 15) + "..." : fileNames.join(", ")) +
									(invalidFiles.length === 1 ? " is an" : " are") +
									" unsupported " +
									(invalidFiles.length === 1 ? "file" : "files") +
									" and " +
									(invalidFiles.length === 1 ? "has" : "have") +
									" been deselected automatically.",
							);
							setOpenSnackbar(true);
						} else {
							setOpenSnackbar(false);
						}
					} else {
						setOpenSnackbar(true);
					}
				}}
			/>

			{/* Quick Replies */}

			{/* The server decides when and which (lib/chat/quickReplies.ts, decision D9, chat audit
			    2.2), the app's rule: page 1 of a thread the viewer can write in, worded for a broadcast
			    or a requirement claim. The old fixed chips showed under the first message of any chat. */}
			{selectedFilePreview.length === 0 && !isBroadcastDeletedOrExpired && (current_chat_data?.quick_replies || []).length > 0 ? (
				<div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
					{(current_chat_data?.quick_replies || []).map((reply: string) => ({ value: reply })).map((chip, index) => (
						<BeegruButton
							key={index}
							flavor="primary"
							variant="contained"
							disableRipple
							disableTouchRipple
							disableFocusRipple
							disableElevation
							onClick={() => {
								// The same guarded send as the composer (chat audit I3).
								sendTextMessage(chip.value);
							}}
							sx={{
								borderRadius: "1rem",
								textTransform: "none",
								textAlign: "left",
								fontSize: "0.825rem",
								lineHeight: "1.25rem",
								padding: "0.5rem 1rem",
								border: "1px solid #e0e0e0",
								"&.MuiButton-contained": {
									background: "#FFFFFF",
									color: theme.palette.text.secondary,
									boxShadow: "none",
									"&:hover": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
									"&:active": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
									"&:focus": {
										background: "#FFFFFF",
										boxShadow: "none",
									},
								},
							}}
						>
							{chip.value}
						</BeegruButton>
					))}
				</div>
			) : null}

			{/* Broadcast Deleted or Expired Alert */}

			{isBroadcastDeletedOrExpired ? (
				<Alert
					severity="error"
					sx={{
						display: "flex",
						flexDirection: "row",
						justifyContent: "flex-start",
						alignItems: "center",
						width: "100%",
						marginTop: "1rem",
						gap: "1rem",
					}}
					action={
						<BeegruButton
							flavor="primary"
							variant="text"
							onClick={() => {
								dispatch(setChatScreenThunk("chat_list"));
							}}
						>
							{router.locale === "ar-AE" ? "العودة" : router.locale === "kn-IN" ? "ಹಿಂದೆ ಹೋಗಿ" : "Go Back"}
						</BeegruButton>
					}
				>
					{router.locale === "ar-AE"
						? "تم حذف البث أو انتهت صلاحيته. لا يمكن إرسال الرسائل."
						: router.locale === "kn-IN"
							? "ಪ್ರಸಾರವನ್ನು ಅಳಿಸಲಾಗಿದೆ ಅಥವಾ ಅವಧಿ ಮುಗಿದಿದೆ. ಸಂದೇಶಗಳನ್ನು ಕಳುಹಿಸಲಾಗುವುದಿಲ್ಲ."
							: "This broadcast has expired !"}
				</Alert>
			) : (
				<div
					style={{
						display: "flex",
						flexDirection: "column",
						width: "100%",
						gap: "0.5rem",
					}}
				>
					{/* A broadcast with no chat yet answers on each card in the thread (MessageSection); the
					    composer works there too: the first message opens the chat (18 Oct 2026). */}
					<div
						style={{
							display: "flex",
							flexDirection: "row",
							width: "100%",
							gap: "0.5rem",
						}}
					>
							<ChatContainer
								sx={{
									display: initial_load_done ? "flex" : "none",
									flexDirection: "column",
								}}
							>
								{/* File Preview Section */}

								{selectedFilePreview.length > 0 && !sendFiles && (
									<PreviewGrid>
										{selectedFilePreview.map((file: string, index: number) => (
											<ImageCard key={index}>
												{file !== "" ? (
													<>
														<StyledPreviewImage
															alt="Property Images"
															src={file}
															referrerPolicy="no-referrer"
														/>
														<IconButton
															size="small"
															onClick={() => handleRemoveImage(index)}
															sx={{
																position: "absolute",
																top: "0.5rem",
																right: "0.5rem",
																background: "rgba(255, 255, 255, 0.9)",
																backdropFilter: "blur(4px)",
																"&:hover": {
																	background: "rgba(255, 255, 255, 1)",
																},
																width: "2rem",
																height: "2rem",
																padding: "0.25rem",
															}}
														>
															<DeleteOutline sx={{ color: "#E31B0C", fontSize: "1.25rem" }} />
														</IconButton>
													</>
												) : (
													<CircularProgress />
												)}
											</ImageCard>
										))}
									</PreviewGrid>
								)}

								{/* Editing a message (18 Oct 2026): its old text, with a cross to stop. */}

								{editing ? (
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											gap: "0.5rem",
											width: "100%",
											padding: "0.375rem 0.625rem",
											borderLeft: "3px solid #FC8019",
											borderRadius: "0.5rem",
											background: "rgba(252, 128, 25, 0.06)",
										}}
									>
										<div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
											<Typography
												variant="caption"
												sx={{ fontWeight: 600, color: "#B05911" }}
											>
												{voiceLang.editing}
											</Typography>
											<Typography
												variant="caption"
												color={editError ? "error" : "text.secondary"}
												sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
											>
												{editError || editing.body}
											</Typography>
										</div>
										<IconButton
											size="small"
											aria-label="Cancel edit"
											onClick={() => dispatch(setChatEditingThunk(null))}
										>
											<CloseOutlined sx={{ fontSize: "1rem", color: "#6c757d" }} />
										</IconButton>
									</div>
								) : null}

								{/* Replying to a message (chat audit I13): the quote, with a cross to stop. */}

								{current_chat_data?.reply_to ? (
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											gap: "0.5rem",
											width: "100%",
											padding: "0.375rem 0.625rem",
											borderLeft: "3px solid #FC8019",
											borderRadius: "0.5rem",
											background: "rgba(252, 128, 25, 0.06)",
										}}
									>
										<div style={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
											<Typography
												variant="caption"
												sx={{ fontWeight: 600, color: "#B05911" }}
											>
												{voiceLang.replying_to} {current_chat_data.reply_to.sender_name}
											</Typography>
											<Typography
												variant="caption"
												color="text.secondary"
												sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
											>
												{current_chat_data.reply_to.body}
											</Typography>
										</div>
										<IconButton
											size="small"
											aria-label="Cancel reply"
											onClick={() => dispatch(setChatReplyToThunk(null))}
										>
											<CloseOutlined sx={{ fontSize: "1rem", color: "#6c757d" }} />
										</IconButton>
									</div>
								) : null}

								{/* A document or voice note on its way, or what failed with Try again. */}

								{attachmentSend.state !== "idle" ? (
									<div
										style={{
											display: "flex",
											flexDirection: "row",
											alignItems: "center",
											justifyContent: "space-between",
											gap: "0.5rem",
											width: "100%",
										}}
									>
										<Typography
											variant="caption"
											sx={{ color: attachmentSend.state === "failed" ? "error.main" : "text.secondary" }}
										>
											{attachmentSend.label}
										</Typography>
										{attachmentSend.state === "sending" ? (
											<CircularProgress size={16} />
										) : (
											<div style={{ display: "flex", gap: "0.25rem" }}>
												<BeegruButton
													variant="text"
													flavor="primary"
													onClick={attachmentSend.retry}
													sx={{ textTransform: "none", padding: "0rem 0.5rem", minWidth: 0 }}
												>
													{voiceLang.try_again}
												</BeegruButton>
												<BeegruButton
													variant="text"
													flavor="primary"
													onClick={attachmentSend.discard}
													sx={{ textTransform: "none", padding: "0rem 0.5rem", minWidth: 0, color: "#6c757d !important" }}
												>
													{voiceLang.discard}
												</BeegruButton>
											</div>
										)}
									</div>
								) : null}

								{voiceNotice ? (
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{ width: "100%" }}
									>
										{voiceNotice}
									</Typography>
								) : null}

								{/* Chat Input Field */}

								<div
									style={{
										display: "flex",
										flexDirection: "row",
										width: "100%",
										position: "relative",
										gap: "0.5rem",
										alignItems: "center",
									}}
								>
									{recordingStartedAt !== null ? (
										/* A recording, WhatsApp's panel (19 Oct 2026, as on the app): the clock and the live
										   waveform on top, then a red delete, a wide Pause / Resume pill and send. */
										<div style={{ display: "flex", flexDirection: "column", gap: "0.75rem", flex: 1, minWidth: 0, padding: "0.25rem 0" }}>
											{/* The clock and the live waveform. */}
											<div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "1rem", minWidth: 0 }}>
												<RecordingDot className={recordingPaused ? "paused" : undefined} />
												<Typography
													variant="body1"
													sx={{ fontSize: "1.125rem", fontWeight: 500, whiteSpace: "nowrap", flexShrink: 0 }}
												>
													{recordingClock}
												</Typography>
												<canvas
													ref={waveformCanvasRef}
													aria-hidden="true"
													style={{ flex: 1, minWidth: 0, height: "1.75rem", display: "block" }}
												/>
											</div>
											{/* Delete, Pause / Resume, send. */}
											<div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "0.625rem" }}>
												<IconButton
													aria-label="Discard voice note"
													onClick={() => stopRecording(true)}
													sx={{ width: "3rem", height: "3rem", flexShrink: 0, background: "#FDECEA", "&:hover": { background: "#FBD9D5" } }}
												>
													<DeleteOutline sx={{ fontSize: "1.375rem", color: "#E31B0C" }} />
												</IconButton>
												<BeegruButton
													variant="outlined"
													flavor="primary"
													size="small"
													onClick={togglePauseRecording}
													startIcon={
														recordingPaused ? (
															<MicNoneOutlined sx={{ fontSize: "1.2rem !important" }} />
														) : (
															<PauseRounded sx={{ fontSize: "1.2rem !important" }} />
														)
													}
													sx={{
														flex: 1,
														height: "3rem",
														borderRadius: "999px",
														textTransform: "none",
														whiteSpace: "nowrap",
														minWidth: 0,
														fontSize: "0.9375rem",
														color: "#495057 !important",
														border: "1px solid #ced4da !important",
													}}
												>
													{recordingPaused ? voiceLang.resume : voiceLang.pause}
												</BeegruButton>
												<IconButton
													aria-label="Send voice note"
													onClick={() => stopRecording(false)}
													sx={{ width: "3rem", height: "3rem", flexShrink: 0, background: "#FC8019", "&:hover": { background: "#E56F0F" } }}
												>
													<SendOutlined sx={{ fontSize: "1.25rem", color: "#FFFFFF" }} />
												</IconButton>
											</div>
										</div>
									) : (
									<TextField
										value={afterUpload ? current_message : message}
										onChange={handleTextFieldChange}
										variant="standard"
										size="small"
										fullWidth
										placeholder={localLang.start_typing}
										sx={{
											"& .MuiInputBase-input::placeholder": {
												fontSize: "0.875rem",
											},
											"& .MuiInputBase-input": {
												fontSize: "0.875rem",
											},
										}}
										slotProps={{
											input: {
												disableUnderline: true,
												autoComplete: "off",
											},
										}}
										onKeyUp={(e) => {
											// With photos picked the typed words are their caption, so Enter sends the photos.
											if (e.key === "Enter" && (afterUpload ? selectedFilePreview.length > 0 && !sendButtonDisabled : message.trim().length > 0)) {
												handleSendMessage();
											}
										}}
									/>

									)}

									{/* No chat yet: text only; files and voice notes are uploaded for a chat. */}
									{chat_source_collection === "broadcast" ? null : recordingStartedAt === null ? (
										<React.Fragment>
									<IconButton
										onClick={() => input.current?.click()}
										sx={{
											height: "1.688rem",
											width: "1.688rem",
										}}
										disabled={sendFiles || messages_loading}
									>
										<AddPhotoAlternateOutlined
											sx={{
												fontSize: "1.2rem",
												color: "#6c757d",
												cursor: "pointer",
											}}
										/>
									</IconButton>
									{/* A document (a PDF), sent as its own message (chat audit I13). */}
									<IconButton
										aria-label="Attach a document"
										onClick={() => docInput.current?.click()}
										sx={{
											height: "1.688rem",
											width: "1.688rem",
										}}
										disabled={sendFiles || messages_loading || attachmentSend.state === "sending"}
									>
										<AttachFileOutlined
											sx={{
												fontSize: "1.2rem",
												color: "#6c757d",
												cursor: "pointer",
											}}
										/>
									</IconButton>
									<input
										ref={docInput}
										type="file"
										accept="application/pdf"
										multiple
										style={{ display: "none" }}
										onChange={handlePickDocuments}
									/>
									{/* Your published listings, up to five (gap A8, the app's "Listing" tile). */}
									<IconButton
										aria-label="Share listings"
										onClick={() => setListingPickerOpen(true)}
										sx={{
											height: "1.688rem",
											width: "1.688rem",
										}}
										disabled={sendFiles || messages_loading || attachmentSend.state === "sending"}
									>
										<ApartmentOutlined
											sx={{
												fontSize: "1.2rem",
												color: "#6c757d",
												cursor: "pointer",
											}}
										/>
									</IconButton>
									<ChatListingPickerDialog
										open={listingPickerOpen}
										onClose={() => setListingPickerOpen(false)}
										profile_context={profile_context}
										onDone={shareListings}
									/>
									<IconButton
										aria-label="Share a location"
										onClick={() => setLocationDialogOpen(true)}
										sx={{
											height: "1.688rem",
											width: "1.688rem",
										}}
										disabled={sendFiles || messages_loading || attachmentSend.state === "sending"}
									>
										<LocationOnOutlined
											sx={{
												fontSize: "1.2rem",
												color: "#6c757d",
												cursor: "pointer",
											}}
										/>
									</IconButton>
									{/* Only a business with a phone of its own has a contact card to share (decision D10;
									    the phone, 19 Oct 2026: fetch-messages' can_share_contact). */}
									{profile_context?.is_business_profile && current_chat_data?.can_share_contact === true ? (
										<IconButton
											aria-label="Share my contact"
											onClick={() => sendSpecial({ share_contact: true })}
											sx={{
												height: "1.688rem",
												width: "1.688rem",
											}}
											disabled={sendFiles || messages_loading || attachmentSend.state === "sending"}
										>
											<ContactPhoneOutlined
												sx={{
													fontSize: "1.2rem",
													color: "#6c757d",
													cursor: "pointer",
												}}
											/>
										</IconButton>
									) : null}
									<ChatLocationDialog
										open={locationDialogOpen}
										onClose={() => setLocationDialogOpen(false)}
										locale={router.locale}
										onSend={(place) => {
											setLocationDialogOpen(false);
											sendSpecial({ location: place });
										}}
									/>
									{/* A voice note: the mic while there is nothing typed or picked. */}
									{message.length === 0 && selectedFilePreview.length === 0 && !afterUpload ? (
										<IconButton
											aria-label="Record a voice note"
											onClick={startRecording}
											sx={{
												height: "1.688rem",
												width: "1.688rem",
											}}
											disabled={sendFiles || messages_loading || attachmentSend.state === "sending"}
										>
											<MicNoneOutlined
												sx={{
													fontSize: "1.25rem",
													color: "#6c757d",
													cursor: "pointer",
												}}
											/>
										</IconButton>
									) : null}
										</React.Fragment>
									) : null}
									<Fade in={selectedFilePreview.length > 0 && !sendButtonDisabled}>
										<AnimatedButtonWrapper
										sx={{
												display: selectedFilePreview.length === 0 || sendButtonDisabled ? "none" : "flex",
											}}
										>
											<BeegruButton
												flavor="success"
												variant="contained"
												onClick={handleSendMessage}
												disabled={selectedFilePreview.length === 0 || messages_loading}
												sx={{
													borderRadius: "1rem",
													transition: "all 0.2s ease-in-out",
												}}
											>
												<SendOutlined
													sx={{
														fontSize: "1rem",
													}}
												/>
											</BeegruButton>
										</AnimatedButtonWrapper>
									</Fade>
								</div>
							</ChatContainer>

							{/* Send Button */}

							<Fade in={!messages_loading && message.length > 0}>
								<AnimatedButtonWrapper
									sx={{
										display: messages_loading || message.length === 0 || afterUpload ? "none" : "flex",
									}}
								>
									<BeegruButton
										flavor="success"
										variant="contained"
										onClick={handleSendMessage}
										disabled={messages_loading || message.length === 0}
										sx={{
											borderRadius: "1rem",
											transition: "all 0.2s ease-in-out",
										}}
									>
										<SendOutlined
											sx={{
												fontSize: "1.5rem",
											}}
										/>
									</BeegruButton>
								</AnimatedButtonWrapper>
							</Fade>
					</div>
				</div>
			)}
		</RootDiv>
	);
};

export default ChatInput;
